import { Button, Group, Modal, Stack } from "@mantine/core";
import { useCallback, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useTRPC } from "../../../../trpc";
import { captureEvent } from "../../../../utils/posthog";
import { Event } from "../../../../constants/events";
import type { Tileset, TilesetVersion } from "../types/tileset";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface SetTilesetVersionModalProps {
  open: boolean;
  onClose: () => void;
  tileset: Tileset;
  version: TilesetVersion;
}

export const SetTilesetVersionModal: FC<SetTilesetVersionModalProps> = ({ onClose, open, tileset, version }) => {
  const {
    id: tilesetId,
    key: tilesetKey,
  } = tileset;

  const {
    id: versionId,
  } = version;

  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutateAsync: setTilesetVersion, isPending } = useMutation(trpc.setTilesetVersion.mutationOptions());

  const handleSetVersion = useCallback(() => {
    captureEvent(Event.TilesetDeleteTileset);

    void setTilesetVersion({ tilesetId, versionId }).then(() => {
      void queryClient.invalidateQueries(trpc.getTilesets.queryFilter());
      void queryClient.invalidateQueries(trpc.getTilesetWithVersions.queryFilter());
      onClose();
    }).catch((error: unknown) => {
      logger.error({ error }, "Failed to set tileset version tileset:");
    });
  }, [setTilesetVersion, tilesetId, versionId, onClose, queryClient]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={intl.formatMessage({ defaultMessage: "Delete tileset", id: "title.deleteTileset" })}
    >
      <Stack gap="xs">
        <FormattedMessage
          id="text.setTilesetVersionDescription"
          defaultMessage="Are you sure you want to set the tileset {tileset} version {version}?"
          values={{ tileset: tilesetKey, version: versionId }}
        />

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="button" loading={isPending} onClick={onClose}>
            <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
          </Button>

          <Button variant="tertiary" type="button" loading={isPending} onClick={handleSetVersion}>
            <FormattedMessage defaultMessage="Change version" id="button.changeVersion" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
